<template>
	<SuiDataTable
		class="task-list"
		:col-definition="colDefinition"
		@click-header="onHeaderClick"
	>
		<!-- Group by phase -->
		<template
			v-if="groupBy === TaskListGroupBy.Phases && visiblePhases.length"
		>
			<!-- tasks without a phase -->
			<TaskListRowTaskBundle
				v-if="
					project &&
					taskListStore.taskBundleOpen === UNGROUPED_TASKS_ID &&
					taskListStore.taskBundlePlacement ===
						TaskBundlePlacement.Top
				"
				:project-id="project.id"
				:project-phases="phases"
				:parent-task-id="parentTaskId"
				:show-subtasks="showSubtasks"
				:col-definition="colDefinition"
			/>

			<template
				v-for="task in groupedTasks[UNGROUPED_TASKS_ID]"
				:key="task.id"
			>
				<TaskListRowMainTask
					:task="task"
					:col-definition="colDefinition"
					:show-subtasks="showSubtasks"
					:subtasks="task.subtasks"
					:subtasks-unsorted="subtasksUnsorted"
					:loading-subtasks="loadingSubtasks"
					:tasks-enrichment-data="tasksEnrichmentData"
					:newly-added-task-ids="newlyAddedTaskIds"
					@task-updated="onTaskUpdated"
					@add-task="
						onAddTask({
							name: $event['name'],
							parentId: task.id,
						})
					"
				/>
				<template v-if="task.id === lastRenderedTaskId">
					<slot name="after-last-task" />
					<template v-if="loadingMore">
						<TaskListRowSkeleton
							v-for="i in SKELETON_LOADING_ROWS_COUNT"
							:key="`load-more-skeleton-${i}`"
							:col-definition="colDefinition"
						/>
					</template>
				</template>
			</template>

			<template
				v-for="task in unsortedGroupedTasks[UNGROUPED_TASKS_ID]"
				:key="task.id"
			>
				<TaskListRowMainTask
					:task="task"
					:col-definition="colDefinition"
					:subtasks="task.subtasks"
					:show-subtasks="showSubtasks"
					:subtasks-unsorted="subtasksUnsorted"
					:loading-subtasks="loadingSubtasks"
					:tasks-enrichment-data="tasksEnrichmentData"
					:newly-added-task-ids="newlyAddedTaskIds"
					@task-updated="onTaskUpdated"
					@add-task="
						onAddTask({
							name: $event['name'],
							parentId: task.id,
						})
					"
				/>
				<template v-if="task.id === lastRenderedTaskId">
					<slot name="after-last-task" />
					<template v-if="loadingMore">
						<TaskListRowSkeleton
							v-for="i in SKELETON_LOADING_ROWS_COUNT"
							:key="`load-more-skeleton-${i}`"
							:col-definition="colDefinition"
						/>
					</template>
				</template>
			</template>

			<template
				v-if="
					loadingRowsGrouped && loadingRowsGrouped[UNGROUPED_TASKS_ID]
				"
			>
				<TaskListRowSkeleton
					v-for="i in loadingRowsGrouped[UNGROUPED_TASKS_ID]"
					:key="i"
					:col-definition="colDefinition"
					:name="i"
				/>
			</template>

			<TaskListRowTaskBundle
				v-if="
					project &&
					taskListStore.taskBundleOpen === UNGROUPED_TASKS_ID &&
					taskListStore.taskBundlePlacement ===
						TaskBundlePlacement.Bottom
				"
				:project-id="project.id"
				:project-phases="phases"
				:parent-task-id="parentTaskId"
				:show-subtasks="showSubtasks"
				:col-definition="colDefinition"
			/>

			<TaskListRowAddTask
				v-if="
					showAddTaskButton &&
					groupedTasks[UNGROUPED_TASKS_ID] &&
					groupedTasks[UNGROUPED_TASKS_ID].length > 0
				"
				:add-task-label="addTaskLabel"
				:show-subtasks="showSubtasks"
				:col-definition="colDefinition"
				@add="onAddTask({ name: $event })"
			/>

			<template v-for="phase in visiblePhases" :key="phase.id">
				<template
					v-if="
						(phase.endDate &&
							resetTime(phase.endDate) >=
								resetTime(Date.now())) ||
						(groupedTasks[phase.id] &&
							groupedTasks[phase.id].length) ||
						!phase.hasTasks
					"
				>
					<TaskListRowPhase
						:project="project"
						:phase="phase"
						:colspan="colDefinition.length"
						@open-phases-modal="$emit('open-phases-modal')"
					/>

					<TaskListRowTaskBundle
						v-if="
							project &&
							taskListStore.taskBundleOpen === phase.id &&
							taskListStore.taskBundlePlacement ===
								TaskBundlePlacement.Top
						"
						:project-id="project.id"
						:project-phase-id="phase.id"
						:project-phases="phases"
						:parent-task-id="parentTaskId"
						:show-subtasks="showSubtasks"
						:col-definition="colDefinition"
					/>

					<template
						v-for="task in groupedTasks[phase.id]"
						:key="task.id"
					>
						<TaskListRowMainTask
							:task="task"
							:col-definition="colDefinition"
							:show-subtasks="showSubtasks"
							:subtasks="task.subtasks"
							:subtasks-unsorted="subtasksUnsorted"
							:loading-subtasks="loadingSubtasks"
							:tasks-enrichment-data="tasksEnrichmentData"
							:newly-added-task-ids="newlyAddedTaskIds"
							@task-updated="onTaskUpdated"
							@add-task="
								onAddTask({
									name: $event['name'],
									projectPhaseId: phase.id,
									parentId: task.id,
								})
							"
						/>
						<template v-if="task.id === lastRenderedTaskId">
							<slot name="after-last-task" />
							<template v-if="loadingMore">
								<TaskListRowSkeleton
									v-for="i in SKELETON_LOADING_ROWS_COUNT"
									:key="`load-more-skeleton-${i}`"
									:col-definition="colDefinition"
								/>
							</template>
						</template>
					</template>

					<template
						v-for="task in unsortedGroupedTasks[phase.id]"
						:key="task.id"
					>
						<TaskListRowMainTask
							:task="task"
							:col-definition="colDefinition"
							:show-subtasks="showSubtasks"
							:subtasks="task.subtasks"
							:subtasks-unsorted="subtasksUnsorted"
							:loading-subtasks="loadingSubtasks"
							:tasks-enrichment-data="tasksEnrichmentData"
							:newly-added-task-ids="newlyAddedTaskIds"
							@task-updated="onTaskUpdated"
							@add-task="
								onAddTask({
									name: $event['name'],
									projectPhaseId: phase.id,
									parentId: task.id,
								})
							"
						/>
						<template v-if="task.id === lastRenderedTaskId">
							<slot name="after-last-task" />
							<template v-if="loadingMore">
								<TaskListRowSkeleton
									v-for="i in SKELETON_LOADING_ROWS_COUNT"
									:key="`load-more-skeleton-${i}`"
									:col-definition="colDefinition"
								/>
							</template>
						</template>
					</template>

					<template
						v-if="
							loadingRowsGrouped && loadingRowsGrouped[phase.id]
						"
					>
						<TaskListRowSkeleton
							v-for="i in loadingRowsGrouped[phase.id]"
							:key="i"
							:col-definition="colDefinition"
							:name="i"
						/>
					</template>

					<TaskListRowTaskBundle
						v-if="
							project &&
							taskListStore.taskBundleOpen === phase.id &&
							taskListStore.taskBundlePlacement ===
								TaskBundlePlacement.Bottom
						"
						:project-id="project.id"
						:project-phase-id="phase.id"
						:project-phases="phases"
						:parent-task-id="parentTaskId"
						:show-subtasks="showSubtasks"
						:col-definition="colDefinition"
					/>

					<TaskListRowAddTask
						v-if="showAddTaskButton"
						:add-task-label="addTaskLabel"
						:show-subtasks="showSubtasks"
						:col-definition="colDefinition"
						:project-phase-id="phase.id"
						@add="
							onAddTask({
								name: $event,
								projectPhaseId: phase.id,
							})
						"
					/>
				</template>
			</template>
		</template>

		<!-- No grouping -->
		<template v-else>
			<TaskListRowTaskBundle
				v-if="
					taskListStore.taskBundleOpen === UNGROUPED_TASKS_ID &&
					taskListStore.taskBundlePlacement ===
						TaskBundlePlacement.Top
				"
				:project-id="project?.id"
				:project-phases="phases"
				:parent-task-id="parentTaskId"
				:show-subtasks="showSubtasks"
				:col-definition="colDefinition"
			/>

			<template v-for="task in visibleSortedTasks" :key="task.id">
				<TaskListRowMainTask
					:task="task"
					:col-definition="colDefinition"
					:subtasks="task.subtasks"
					:show-subtasks="showSubtasks"
					:subtasks-unsorted="subtasksUnsorted"
					:loading-subtasks="loadingSubtasks"
					:tasks-enrichment-data="tasksEnrichmentData"
					:newly-added-task-ids="newlyAddedTaskIds"
					@task-updated="onTaskUpdated"
					@add-task="
						onAddTask({
							name: $event['name'],
							parentId: task.id,
						})
					"
				/>
				<template v-if="task.id === lastRenderedTaskId">
					<slot name="after-last-task" />
					<template v-if="loadingMore">
						<TaskListRowSkeleton
							v-for="i in SKELETON_LOADING_ROWS_COUNT"
							:key="`load-more-skeleton-${i}`"
							:col-definition="colDefinition"
						/>
					</template>
				</template>
			</template>

			<template v-for="task in visibleUnSortedTasks" :key="task.id">
				<TaskListRowMainTask
					:task="task"
					:col-definition="colDefinition"
					:subtasks="task.subtasks"
					:show-subtasks="showSubtasks"
					:subtasks-unsorted="subtasksUnsorted"
					:loading-subtasks="loadingSubtasks"
					:tasks-enrichment-data="tasksEnrichmentData"
					:newly-added-task-ids="newlyAddedTaskIds"
					@task-updated="onTaskUpdated"
					@add-task="
						onAddTask({
							name: $event['name'],
							parentId: task.id,
						})
					"
				/>
				<template v-if="task.id === lastRenderedTaskId">
					<slot name="after-last-task" />
					<template v-if="loadingMore">
						<TaskListRowSkeleton
							v-for="i in SKELETON_LOADING_ROWS_COUNT"
							:key="`load-more-skeleton-${i}`"
							:col-definition="colDefinition"
						/>
					</template>
				</template>
			</template>

			<template
				v-if="
					loadingRowsGrouped && loadingRowsGrouped[UNGROUPED_TASKS_ID]
				"
			>
				<TaskListRowSkeleton
					v-for="i in loadingRowsGrouped[UNGROUPED_TASKS_ID]"
					:key="i"
					:col-definition="colDefinition"
					:name="i"
				/>
			</template>

			<TaskListRowTaskBundle
				v-if="
					taskListStore.taskBundleOpen === UNGROUPED_TASKS_ID &&
					taskListStore.taskBundlePlacement ===
						TaskBundlePlacement.Bottom
				"
				:project-id="project?.id"
				:project-phases="phases"
				:parent-task-id="parentTaskId"
				:show-subtasks="showSubtasks"
				:col-definition="colDefinition"
			/>

			<TaskListRowAddTask
				v-if="showAddTaskButton"
				:show-totals="showTotals"
				:add-task-label="addTaskLabel"
				:show-subtasks="showSubtasks"
				:col-definition="colDefinition"
				:tasks="visibleTasks"
				@add="onAddTask({ name: $event })"
			/>
		</template>

		<template v-if="loading">
			<TaskListRowSkeleton
				v-for="i in SKELETON_LOADING_ROWS_COUNT"
				:key="i"
				:col-definition="colDefinition"
			/>
		</template>

		<template v-if="lastRenderedTaskId === null">
			<slot name="after-last-task" />
			<template v-if="loadingMore">
				<TaskListRowSkeleton
					v-for="i in SKELETON_LOADING_ROWS_COUNT"
					:key="`load-more-skeleton-${i}`"
					:col-definition="colDefinition"
				/>
			</template>
		</template>
	</SuiDataTable>

	<TaskListEmptyState
		v-if="isEmptyStateVisible"
		:project="project"
		:task-done-filter="taskDoneFilter"
	/>
</template>

<script setup lang="ts">
import { computed, ref, toRefs } from 'vue';
import { usePermissions, useSocket } from '@/composables';
import {
	type Task,
	TaskListTaskDocument,
	useTaskListTaskLazyQuery,
	type TaskListPhaseFragment,
	type TaskListTaskFragment,
	type TaskListEnrichmentFieldsFragment,
	type ProjectPhase,
	type TaskListProjectFragment,
	ProjectPhaseTypeEnum,
	TYPES,
} from '@/generated/graphql';
import {
	SuiDataTable,
	SuiDataTableSortDirection,
	type SuiDataTableColumnType,
} from '@developers/scoro-ui';
import { useTaskList } from './useTaskList';
import { TaskListRowPhase, TaskListRowMainTask } from './TaskListRow';
import { SocketMessageType } from '@/constants/socketMessages';
import TaskListRowAddTask from './TaskListRow/TaskListRowAddTask.vue';
import {
	type SortState,
	TaskBundlePlacement,
	TaskListGroupBy,
	type TaskListLoadingRows,
	type TaskListLoadingSubtaskRows,
	type TasListTaskOrLimited,
} from './types';
import groupByFn from 'lodash/groupBy';
import { resetTime } from '@/utils/formatting';
import {
	COLUMN_HEADER_SORT_MAP,
	TABLE_COLUMN,
	UNGROUPED_TASKS_ID,
} from './constants';
import { type ShowEventPopupPayload } from '@/composables/useMfeUtils';
import TaskListRowSkeleton from './TaskListRow/TaskListRowSkeleton.vue';
import TaskListRowTaskBundle from './TaskListRow/TaskListRowTaskBundle.vue';
import { TASK_DONE_FILTER } from '@/features/Task/constants';
import { type ValueOf } from '@/types';
import { useTaskListStore } from './store';
import { PermissionAction } from '@/constants/permissions';
import { Constants } from '@/constants';
import TaskListEmptyState from '@/features/Task/components/TaskList/TaskListEmptyState/TaskListEmptyState.vue';
import { useLocalStorage } from '@vueuse/core';
import { LocalStorageKeys } from '@/constants/storage';
import {
	type BaseSocketMessage,
	type SocketTaskPayload,
} from '@/composables/socket';

const SKELETON_LOADING_ROWS_COUNT = 2;

export type TaskListProps = {
	tasksEnrichmentData?: Map<
		Task['id'],
		TaskListEnrichmentFieldsFragment
	> | null;
	newlyAddedTaskIds?: Set<string>;
	tasksSorted: TasListTaskOrLimited[];
	tasksUnsorted: TasListTaskOrLimited[];
	subtasksUnsorted?: Record<Task['id'], TasListTaskOrLimited[]>;
	useTaskStatuses: boolean;
	sort: SortState;
	project?: TaskListProjectFragment;
	phases?: TaskListPhaseFragment[];
	groupBy?: TaskListGroupBy;
	loading?: boolean;
	loadingMore?: boolean;
	loadingRowsGrouped?: TaskListLoadingRows;
	loadingSubtasks?: TaskListLoadingSubtaskRows;
	taskDoneFilter?: ValueOf<typeof TASK_DONE_FILTER>;
	addTaskEnabled?: boolean;
	addTaskLabel?: string;
	hideEmptyState?: boolean;
	parentTaskId?: Task['id'];
	showSubtasks?: boolean;
	storageKey?: string;
	availableColumns?: readonly string[];
	showTotals?: boolean;
};

const props = defineProps<TaskListProps>();

const {
	tasksSorted,
	tasksUnsorted,
	subtasksUnsorted,
	phases,
	showSubtasks,
	useTaskStatuses,
} = toRefs(props);

const emit = defineEmits<{
	(e: 'update:sort', payload: SortState): void;
	(
		e: 'add-task',
		payload: {
			name: Task['name'];
			projectPhaseId?: ProjectPhase['id'];
			parentId?: Task['id'];
		}
	): void;
	(e: 'show-event-popup', payload: ShowEventPopupPayload): void;
	(e: 'open-phases-modal'): void;
}>();

const { colDefinition } = useTaskList({
	sortState: props.sort,
	showSubtasks,
	useTaskStatuses: useTaskStatuses.value,
	storageKey: props.storageKey,
	availableColumns: props.availableColumns,
});
const taskListStore = useTaskListStore();
const openTaskIds = useLocalStorage(
	LocalStorageKeys.SubtaskListOpenTasks,
	[] as Array<Task['id']>
);
const { can } = usePermissions();

const lastFetchedTaskId = ref<Task['id']>();

const visiblePhases = computed(
	() =>
		phases.value?.filter(
			({ type }) => type === ProjectPhaseTypeEnum.Phase
		) || []
);

const { load, refetch, onResult } = useTaskListTaskLazyQuery(
	{
		id: '',
	},
	{
		fetchPolicy: 'network-only',
	}
);

const onTaskUpdated = (updatedTask?: TaskListTaskFragment) => {
	if (!updatedTask) {
		return;
	}

	const currentIndex = tasksUnsorted.value.findIndex(
		({ id }) => id === updatedTask.id
	);

	if (currentIndex !== -1) {
		tasksUnsorted.value[currentIndex] = updatedTask;
	}

	if (subtasksUnsorted.value && subtasksUnsorted.value[updatedTask.id]) {
		subtasksUnsorted.value[updatedTask.id] = [];
	}
};

onResult((result) => {
	if (result.data?.Task) {
		onTaskUpdated(result.data.Task);
	}
});

const reloadTaskById = async (taskId: Task['id']) => {
	if (lastFetchedTaskId.value === taskId) {
		await refetch();
	} else {
		lastFetchedTaskId.value = taskId;
		load(TaskListTaskDocument, {
			id: taskId,
		});
	}
};

const shouldHandleTaskMessage = (
	message:
		| BaseSocketMessage<'newTaskAdded', SocketTaskPayload>
		| BaseSocketMessage<'taskChanged', SocketTaskPayload>
): boolean => {
	const projectId = String(message.data.project_id);
	const parentId = message.data.parent_id
		? String(message.data.parent_id)
		: undefined;

	if (props.project && props.project.id !== projectId) {
		return false;
	}

	if (props.parentTaskId && props.parentTaskId !== parentId) {
		return false;
	}

	return true;
};

useSocket({
	[SocketMessageType.TASK_CREATED]: async (message) => {
		if (!shouldHandleTaskMessage(message)) {
			return;
		}

		const parentId = message.data.parent_id
			? String(message.data.parent_id)
			: undefined;

		if (props.showSubtasks && parentId) {
			reloadTaskById(parentId);
		}
	},
	[SocketMessageType.TASK_UPDATED]: async (message) => {
		if (!shouldHandleTaskMessage(message)) {
			return;
		}

		const taskId = String(message.data.event_id);
		const parentId = message.data.parent_id
			? String(message.data.parent_id)
			: undefined;

		if (props.showSubtasks && parentId) {
			reloadTaskById(parentId);
		} else {
			reloadTaskById(taskId);
		}
	},
});

const visibleSortedTasks = computed(() =>
	tasksSorted.value.filter((task) => {
		if (props.taskDoneFilter === TASK_DONE_FILTER.Done) {
			return task.isDone;
		} else if (props.taskDoneFilter === TASK_DONE_FILTER.NotDone) {
			return !task.isDone;
		} else {
			return true;
		}
	})
);

const visibleUnSortedTasks = computed(() =>
	tasksUnsorted.value.filter((task) => {
		if (props.taskDoneFilter === TASK_DONE_FILTER.Done) {
			return task.isDone;
		} else if (props.taskDoneFilter === TASK_DONE_FILTER.NotDone) {
			return !task.isDone;
		} else {
			return true;
		}
	})
);

const groupedTasks = computed(() =>
	groupByFn(
		visibleSortedTasks.value,
		(task) =>
			(task.__typename === TYPES.Task && task.projectPhase?.id) ||
			UNGROUPED_TASKS_ID
	)
);

const unsortedGroupedTasks = computed(() =>
	groupByFn(
		visibleUnSortedTasks.value,
		(task) =>
			(task.__typename === TYPES.Task && task.projectPhase?.id) ||
			UNGROUPED_TASKS_ID
	)
);

const onHeaderClick = (sortColumnId: SuiDataTableColumnType['id']) => {
	if (sortColumnId === TABLE_COLUMN.Collapsible) {
		const taskIds = [
			...visibleSortedTasks.value.map((task) => task.id),
			...visibleUnSortedTasks.value.map((task) => task.id),
		];

		const openTaskIdsMatchAll = taskIds.every((id) =>
			openTaskIds.value.includes(id)
		);

		const collapseAll = openTaskIdsMatchAll;

		if (collapseAll) {
			openTaskIds.value = openTaskIds.value.filter(
				(id) => !taskIds.includes(id)
			);
		} else {
			openTaskIds.value = [
				...new Set([...openTaskIds.value, ...taskIds]),
			];
		}

		return;
	}

	if (!(sortColumnId in COLUMN_HEADER_SORT_MAP)) {
		return;
	}

	const alternateDirection =
		props.sort.direction === SuiDataTableSortDirection.ASC
			? SuiDataTableSortDirection.DESC
			: SuiDataTableSortDirection.ASC;

	const updatedSortState: SortState = {
		columnId: sortColumnId,
		direction:
			props.sort.columnId === sortColumnId
				? alternateDirection
				: props.sort.direction,
	};

	emit('update:sort', updatedSortState);
};

const onAddTask = async (payload: {
	name: Task['name'];
	projectPhaseId?: ProjectPhase['id'];
	parentId?: Task['id'];
}) => {
	emit('add-task', payload);
};

const canCreateTasks = computed(() =>
	can(PermissionAction.CREATE, Constants.Type.Task)
);

const showAddTaskButton = computed(
	() =>
		canCreateTasks.value &&
		props.taskDoneFilter !== TASK_DONE_FILTER.Done &&
		!props.loading &&
		props.addTaskEnabled
);

const isEmptyStateVisible = computed(() => {
	if (props.loading || props.hideEmptyState) {
		return false;
	}

	return (
		visibleSortedTasks.value.length === 0 &&
		visibleUnSortedTasks.value.length === 0
	);
});

const visibleTasks = computed(() => [
	...visibleSortedTasks.value,
	...visibleUnSortedTasks.value,
]);

const lastRenderedTaskId = computed<Task['id'] | null>(() => {
	if (
		props.groupBy === TaskListGroupBy.Phases &&
		visiblePhases.value.length
	) {
		for (let i = visiblePhases.value.length - 1; i >= 0; i--) {
			const phaseId = visiblePhases.value[i].id;
			const unsorted = unsortedGroupedTasks.value[phaseId];
			if (unsorted && unsorted.length) {
				return unsorted[unsorted.length - 1].id;
			}
			const sorted = groupedTasks.value[phaseId];
			if (sorted && sorted.length) {
				return sorted[sorted.length - 1].id;
			}
		}

		const ungroupedUnsorted =
			unsortedGroupedTasks.value[UNGROUPED_TASKS_ID];
		if (ungroupedUnsorted && ungroupedUnsorted.length) {
			return ungroupedUnsorted[ungroupedUnsorted.length - 1].id;
		}
		const ungroupedSorted = groupedTasks.value[UNGROUPED_TASKS_ID];
		if (ungroupedSorted && ungroupedSorted.length) {
			return ungroupedSorted[ungroupedSorted.length - 1].id;
		}
		return null;
	}

	if (visibleUnSortedTasks.value.length) {
		return visibleUnSortedTasks.value[visibleUnSortedTasks.value.length - 1]
			.id;
	}
	if (visibleSortedTasks.value.length) {
		return visibleSortedTasks.value[visibleSortedTasks.value.length - 1].id;
	}
	return null;
});
</script>

<style lang="scss">
.task-list {
	--sui-data-table-side-spacing: 1rem;

	&__expand-collapse,
	&__assignee {
		--sui-data-table-header-cell-padding: 1.5rem 1rem;
	}
}
</style>
